import { type SimulatorSessionInfo, type TestSuiteData } from "./types";
import { atomWithMutation } from "jotai-tanstack-query";
import { retryOperation } from "./helpers";
import axios from "axios";
import { APPIUM_MIDDLEWARE_URL } from "@/constants/aws-constants"; // Adjust the import path as needed

interface Device {
  screen: { width: number; height: number };
  platform: string;
}

interface AppiumSession {
  stop: () => Promise<void>;
  checkIfSessionIsRunning: () => Promise<boolean>;
  getUI: () => Promise<any>;
  getScreenshot: () => Promise<string>;
  device: Device;
  startedAt: number;
  info: SimulatorSessionInfo;
  serverUrl: string;
  launchApp: () => Promise<void>;
  commands: {
    biometry: (match: boolean) => Promise<void>;
    tap: ({
      coordinates,
      duration,
    }: {
      coordinates: { x: number; y: number };
      duration: number;
    }) => Promise<void>;
    tapOnElement: ({
      text,
      id,
      duration,
    }: {
      text?: string;
      id?: string;
      duration: number;
    }) => Promise<void>;
    type: ({ text }: { text: string }) => Promise<void>;
    keyStroke: ({ keycode }: { keycode: number }) => Promise<void>;
    openDeepLinkUrl: (url: string) => Promise<void>;
    swipe: ({
      startCoordinates,
      endCoordinates,
      duration,
    }: {
      startCoordinates: { x: number; y: number };
      endCoordinates: { x: number; y: number };
      duration?: number;
    }) => Promise<void>;
    zoom: (direction: "in" | "out") => Promise<void>;
    tapHomeScreen: () => Promise<void>;
    shakeDevice: () => Promise<void>;
    doubleTap: ({
      coordinates,
      duration,
      count,
    }: {
      coordinates: { x: number; y: number };
      duration: number;
      count: number;
    }) => Promise<void>;
    multiTap: ({
      coordinates,
      count,
    }: {
      coordinates: { x: number; y: number };
      count: number;
    }) => Promise<void>;
    longPress: ({
      coordinates,
      duration,
      count,
    }: {
      coordinates: { x: number; y: number };
      duration: number;
      count: number;
    }) => Promise<void>;
  };
}

interface CreateAppiumSessionMutationAtomParams {
  platform: string;
  device: string;
  os: string;
  buildId: string;
  bundleId: string;
  storagePath: string;
  language?: string;
  countryCode?: string;
  proxy?: string;
  recordVideo?: boolean;
  onCleanup: () => void;
  organisationId: string;
  testSuite?: TestSuiteData;
  testRunId?: string;
  testTitle?: string;
  grantPermissions: boolean;
}

const appiumSessionMutationAtom = atomWithMutation((get) => ({
  mutationKey: ["createAppiumSessionMutation"],
  mutationFn: createAppiumSession,
}));

const appiumMiddlewareAxios = axios.create({
  baseURL: APPIUM_MIDDLEWARE_URL,
});

const createAppiumSession = async ({
  platform,
  device,
  os,
  buildId,
  bundleId,
  language,
  countryCode,
  proxy,
  recordVideo = true,
  onCleanup,
  organisationId,
  testRunId,
  testSuite,
  testTitle,
  grantPermissions,
}: CreateAppiumSessionMutationAtomParams): Promise<AppiumSession> => {
  let sessionId: string | null = null;

  try {
    let parsedLocale;
    if (platform.toLowerCase() === "ios" && language && countryCode) {
      parsedLocale = `${language.toLowerCase()}_${countryCode.toUpperCase()}`;
    } else {
      parsedLocale = language?.toLowerCase();
    }

    const capabilities = {
      alwaysMatch: {
        platformName: platform,
        "appium:deviceName": device,
        "appium:platformVersion": os,
        [platform.toLowerCase() === "ios"
          ? "appium:autoAcceptAlerts"
          : "appium:autoGrantPermissions"]: grantPermissions,
        "appium:automationName":
          platform.toLowerCase() === "ios" ? "XCUITest" : "UIAutomator2",
        ...(language && { "appium:language": language.toUpperCase() }),
        ...(parsedLocale && { "appium:locale": parsedLocale }),
        "bstack:options": {
          projectName: `Organisation: ${organisationId}`,
          buildName: `TestSuite: ${testSuite?.id || "Manual"}`,
          sessionName: `${testTitle} (id: ${testRunId})`,
          debug: true,
          networkLogs: true,
          deviceLogs: true,
          video: recordVideo,
        },
      },
    };

    // Create BrowserStack session using Cloud Function
    const sessionResponse = await retryOperation(
      async () => {
        try {
          const response = await appiumMiddlewareAxios.post("/session", {
            gptDriverBuildId: buildId,
            ...(testSuite?.deviceProviderSettings != null && {
              deviceProviderSettings: testSuite.deviceProviderSettings,
            }),
            capabilities,
          });

          const date = new Date();
          const heartbeatElement = document.getElementById("heartbeat");
          heartbeatElement.innerText = `STARTING_SESSION_FROM_AUTORUN_QUERY_AFTER_UPLOAD_TO_BROWSERSTACK,${date.getTime()}`;

          // Cloud Function returns just the sessionId
          return response;
        } catch (e: any) {
          if (e?.code === "unauthenticated") {
            throw {
              ...e,
              cancelRetry: true,
              message: "Invalid BrowserStack credentials",
            };
          } else if (e?.code === "invalid-argument") {
            throw {
              ...e,
              cancelRetry: true,
              message: "Invalid device configuration",
            };
          }
          throw e;
        }
      },
      10000,
      3
    );

    sessionId = sessionResponse.data.sessionId;
    // For simplicity, we're mocking the sessionCapabilities
    const sessionCapabilities = {
      // Add necessary capabilities that you would normally get from the session response
      platformName: platform,
      deviceName: device,
      automationName:
        platform.toLowerCase() === "ios" ? "XCUITest" : "UIAutomator2",
    };

    // Helper function to execute commands via the Cloud Function
    const executeAppiumCommand = async (
      method: string,
      path: string,
      body?: any
    ) => {
      const response = await appiumMiddlewareAxios.post("execute-command", {
        method,
        url: `/session/${sessionId}${path}`,
        body,
      });
      return response.data;
    };

    // Helper function to find element - now using Cloud Function
    const findElement = async (locator: {
      strategy: string;
      value: string;
    }) => {
      const response = await executeAppiumCommand("POST", "/element", {
        using: locator.strategy,
        value: locator.value,
      });
      return (
        response.data["element-6066-11e4-a52e-4f735466cecf"] ||
        response.data.ELEMENT
      );
    };

    // Get device dimensions
    const getDeviceDimensions = async () => {
      try {
        const windowSize = await executeAppiumCommand("GET", "/window/rect");
        console.log("windowSize", windowSize);
        return {
          width: windowSize.data.width,
          height: windowSize.data.height,
        };
      } catch {
        // Fallback dimensions based on device
        return { width: 375, height: 812 };
      }
    };

    const deviceDimensions = await getDeviceDimensions();
    const BS_HUB_URL = "https://hub-cloud.browserstack.com/wd/hub";

    return {
      serverUrl: BS_HUB_URL,
      info: {
        sessionId,
        capabilities: sessionCapabilities,
        state: "assigned",
      } as SimulatorSessionInfo,
      startedAt: new Date().getTime(),
      device: {
        screen: deviceDimensions,
        platform,
      },
      checkIfSessionIsRunning: async () => {
        try {
          const response = await appiumMiddlewareAxios.get(
            `check-session-is-running/${sessionId!}`
          );
          return response.data.isRunning;
        } catch {
          return false;
        }
      },
      // TODO only pass run id and suite id for cloud executions
      stop: async () => {
        try {
          await appiumMiddlewareAxios.post("/stop-session", {
            sessionId,
            suiteId: testSuite?.id,
            runId: testRunId,
          });
        } catch (error) {
          console.error("Error stopping session:", error);
        } finally {
          onCleanup();
        }
      },
      getScreenshot: async () => {
        const response = await executeAppiumCommand("GET", "/screenshot");
        return `data:image/jpg;base64, ${response.data}`;
      },
      getUI: async () => {
        const response = await executeAppiumCommand("GET", "/source");
        return response.data;
      },
      launchApp: async () => {
        // For iOS, use activateApp
        if (platform.toLowerCase() === "ios") {
          await executeAppiumCommand("POST", "/appium/device/activate_app", {
            bundleId,
          });
        } else {
          // For Android, use currentPackage/currentActivity
          await executeAppiumCommand("POST", "/appium/device/start_activity", {
            appPackage: bundleId,
            appActivity: ".MainActivity", // You might need to make this configurable
          });
        }
      },
      commands: {
        biometry: async (match: boolean) => {
          // BrowserStack doesn't have direct biometric API, but you can use touch/face ID simulation
          if (platform.toLowerCase() === "ios") {
            await executeAppiumCommand("POST", "/appium/simulator/touch_id", {
              match,
            });
          } else {
            // For Android, you might need to interact with the UI
            console.warn(
              "Biometric authentication not directly supported on Android via REST API"
            );
          }
        },
        shakeDevice: async () => {
          await executeAppiumCommand("POST", "/appium/device/shake");
        },
        openDeepLinkUrl: async (url: string) => {
          if (platform.toLowerCase() === "ios") {
            // For iOS, use mobile: deepLink
            await executeAppiumCommand("POST", "/url", { url });
          } else {
            // For Android
            await executeAppiumCommand(
              "POST",
              "/appium/device/start_activity",
              {
                appPackage: "android",
                appActivity: "android.intent.action.VIEW",
                appWaitActivity: "*",
                intentAction: "android.intent.action.VIEW",
                intentCategory: "android.intent.category.BROWSABLE",
                intentFlags: "0x00000020",
                optionalIntentArguments: `-d "${url}"`,
              }
            );
          }
        },
        tap: async ({ coordinates, duration }) => {
          // Use W3C Actions API
          await executeAppiumCommand("POST", "/actions", {
            actions: [
              {
                type: "pointer",
                id: "finger1",
                parameters: { pointerType: "touch" },
                actions: [
                  {
                    type: "pointerMove",
                    duration: 0,
                    x: coordinates.x,
                    y: coordinates.y,
                  },
                  { type: "pointerDown", button: 0 },
                  { type: "pause", duration: 200 },
                  { type: "pointerUp", button: 0 },
                ],
              },
            ],
          });
        },
        tapOnElement: async ({ text, id, duration }) => {
          try {
            let elementId;

            if (id) {
              // Try to find by resource-id (Android) or accessibility id (iOS)
              try {
                elementId = await findElement({
                  strategy: "id",
                  value: id,
                });
              } catch {
                // Fallback to accessibility id
                elementId = await findElement({
                  strategy: "accessibility id",
                  value: id,
                });
              }
            } else if (text) {
              // Find by text - try different strategies
              try {
                // Try exact text match first
                if (platform.toLowerCase() === "ios") {
                  elementId = await findElement({
                    strategy: "-ios predicate string",
                    value: `label == "${text}" OR name == "${text}" OR value == "${text}"`,
                  });
                } else {
                  elementId = await findElement({
                    strategy: "-android uiautomator",
                    value: `new UiSelector().text("${text}")`,
                  });
                }
              } catch {
                // Try partial match
                if (platform.toLowerCase() === "ios") {
                  elementId = await findElement({
                    strategy: "-ios predicate string",
                    value: `label CONTAINS "${text}" OR name CONTAINS "${text}" OR value CONTAINS "${text}"`,
                  });
                } else {
                  elementId = await findElement({
                    strategy: "-android uiautomator",
                    value: `new UiSelector().textContains("${text}")`,
                  });
                }
              }
            }

            if (!elementId) {
              throw new Error(
                `Element not found with ${text ? `text: ${text}` : `id: ${id}`}`
              );
            }

            // Click the element
            await executeAppiumCommand("POST", `/element/${elementId}/click`);
          } catch (error: any) {
            const identifier = text ?? id ?? "unknown";
            throw new Error(`Element [${identifier}] not found!`);
          }
        },
        doubleTap: async ({ coordinates, duration, count = 2 }) => {
          const actions = [];
          for (let i = 0; i < count; i++) {
            actions.push(
              {
                type: "pointerMove",
                duration: 0,
                x: coordinates.x,
                y: coordinates.y,
              },
              { type: "pointerDown", button: 0 },
              { type: "pause", duration: 50 },
              { type: "pointerUp", button: 0 },
              { type: "pause", duration: 50 }
            );
          }

          await executeAppiumCommand("POST", "/actions", {
            actions: [
              {
                type: "pointer",
                id: "finger1",
                parameters: { pointerType: "touch" },
                actions,
              },
            ],
          });
        },
        multiTap: async ({ coordinates, count }) => {
          const actions = [];
          for (let i = 0; i < count; i++) {
            actions.push(
              {
                type: "pointerMove",
                duration: 0,
                x: coordinates.x,
                y: coordinates.y,
              },
              { type: "pointerDown", button: 0 },
              { type: "pause", duration: 50 },
              { type: "pointerUp", button: 0 },
              { type: "pause", duration: 50 }
            );
          }

          await executeAppiumCommand("POST", "/actions", {
            actions: [
              {
                type: "pointer",
                id: "finger1",
                parameters: { pointerType: "touch" },
                actions,
              },
            ],
          });
        },
        longPress: async ({ coordinates, duration }) => {
          await executeAppiumCommand("POST", "/actions", {
            actions: [
              {
                type: "pointer",
                id: "finger1",
                parameters: { pointerType: "touch" },
                actions: [
                  {
                    type: "pointerMove",
                    duration: 0,
                    x: coordinates.x,
                    y: coordinates.y,
                  },
                  { type: "pointerDown", button: 0 },
                  { type: "pause", duration },
                  { type: "pointerUp", button: 0 },
                ],
              },
            ],
          });
        },
        swipe: async ({
          startCoordinates,
          endCoordinates,
          duration = 1000,
        }) => {
          await executeAppiumCommand("POST", "/actions", {
            actions: [
              {
                type: "pointer",
                id: "finger1",
                parameters: { pointerType: "touch" },
                actions: [
                  {
                    type: "pointerMove",
                    duration: 0,
                    x: startCoordinates.x,
                    y: startCoordinates.y,
                  },
                  { type: "pointerDown", button: 0 },
                  { type: "pause", duration: 50 },
                  {
                    type: "pointerMove",
                    duration,
                    x: endCoordinates.x,
                    y: endCoordinates.y,
                  },
                  { type: "pointerUp", button: 0 },
                ],
              },
            ],
          });
        },
        zoom: async (direction: "in" | "out") => {
          const { width, height } = deviceDimensions;

          // Center of screen
          const cx = Math.floor(width / 2);
          const cy = Math.floor(height / 2);

          // 20% of screen dimensions
          const spreadX = Math.floor(width * 0.2);
          const spreadY = Math.floor(height * 0.2);

          // Gesture area centered on screen
          const left = cx - spreadX;
          const top = cy - spreadY;
          const areaWidth = spreadX * 2;
          const areaHeight = spreadY * 2;

          console.log(
            `[Zoom] dir=${direction} center=(${cx},${cy}) area=(${left},${top}) ${areaWidth}x${areaHeight}`
          );

          if (direction === "in") {
            // Zoom In: fingers spread from center outward
            await executeAppiumCommand("POST", "/execute/sync", {
              script: "mobile: pinchOpenGesture",
              args: [
                {
                  left,
                  top,
                  width: areaWidth,
                  height: areaHeight,
                  percent: 0.5,
                  speed: 800,
                },
              ],
            });
          } else {
            // Zoom Out: fingers come together toward center
            await executeAppiumCommand("POST", "/execute/sync", {
              script: "mobile: pinchCloseGesture",
              args: [
                {
                  left,
                  top,
                  width: areaWidth,
                  height: areaHeight,
                  percent: 1.0,
                  speed: 1500,
                },
              ],
            });
          }
        },
        type: async ({ text }) => {
          // First, we need to find the active element or click on an input field
          try {
            const activeElement = await executeAppiumCommand(
              "GET",
              "/element/active"
            );
            const elementId =
              activeElement.data["element-6066-11e4-a52e-4f735466cecf"] ||
              activeElement.data.ELEMENT;

            // Send keys to the active element
            await executeAppiumCommand("POST", `/element/${elementId}/value`, {
              text,
            });
          } catch {
            // If no active element, try to send keys to the session
            await executeAppiumCommand("POST", "/keys", {
              value: text.split(""),
            });
          }
        },
        keyStroke: async ({ keycode }) => {
          // Map common keycodes to W3C key values
          const keyMap: Record<number, string> = {
            13: "\uE007", // Enter
            8: "\uE003", // Backspace
            9: "\uE004", // Tab
            27: "\uE00C", // Escape
            32: " ", // Space
            37: "\uE012", // Arrow Left
            38: "\uE013", // Arrow Up
            39: "\uE014", // Arrow Right
            40: "\uE015", // Arrow Down
            // Add more mappings as needed
          };

          const key = keyMap[keycode] || String.fromCharCode(keycode);

          await executeAppiumCommand("POST", "/keys", {
            value: [key],
          });
        },
        tapHomeScreen: async () => {
          if (platform.toLowerCase() === "ios") {
            // For iOS, press home button
            await executeAppiumCommand("POST", "/execute/sync", {
              script: "mobile: pressButton",
              args: [{ name: "home" }],
            });
          } else {
            // For Android, use keyevent
            await executeAppiumCommand("POST", "/appium/device/key_event", {
              keycode: 3, // HOME key
            });
          }
        },
      },
    } as AppiumSession;
  } catch (error) {
    console.error("Error creating BrowserStack session:", error);

    // Clean up if session was created
    // if (sessionId) {
    //   try {
    //     await executeCommandFunc({
    //       method: "DELETE",
    //       url: `/session/${sessionId}`,
    //     });
    //   } catch (cleanupError) {
    //     console.error("Error cleaning up session:", cleanupError);
    //   }
    // }

    throw error;
  }
};

export { appiumSessionMutationAtom, type AppiumSession, type Device };
